import type { ReactNode } from 'react';
import { ApiError, authClient, type Me, PLAYER_SITE } from '../lib/api';
import { DeveloperProvider } from '../lib/developer';
import { useApi } from '../lib/use-api';
import { Brand } from './Brand';
import { buttonClass, Loading } from './ui';

function Notice({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-dvh">
      <header className="border-b border-divider bg-card">
        <div className="mx-auto flex max-w-[1360px] items-center px-4 py-3 sm:px-6">
          <Brand />
        </div>
      </header>
      <main className="flex justify-center px-4 py-12 sm:py-16">
        <div className="w-full max-w-md rounded-[8px] border border-line bg-card p-8">
          <p className="text-xs font-semibold tracking-[0.2em] text-muted uppercase">{eyebrow}</p>
          <h1 className="mt-2.5 text-2xl font-semibold">{title}</h1>
          <div className="mt-3 space-y-6 leading-relaxed text-muted">{children}</div>
        </div>
      </main>
    </div>
  );
}

const reload = () => window.location.reload();

/**
 * Lets only developers with two-factor through (the API enforces the same on every request);
 * otherwise explains what's missing and where to fix it.
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const me = useApi<Me>('/v1/me');

  if (me.status === 'loading') return <Loading label="Checking your access…" />;

  if (me.status === 'error') {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return (
        <Notice eyebrow="Signed out" title="Sign in to continue">
          <p>
            The developer dashboard uses your MOGZO account. Sign in on mogzo.com, then come back
            here.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href={`${PLAYER_SITE}/sign-in`} className={buttonClass('primary')}>
              Sign in on mogzo.com
            </a>
            <button type="button" onClick={reload} className={buttonClass('secondary')}>
              I’ve signed in
            </button>
          </div>
        </Notice>
      );
    }
    return (
      <Notice eyebrow="Something went wrong" title="Couldn’t check your access">
        <p>{me.error.message}</p>
        <button type="button" onClick={reload} className={buttonClass('primary')}>
          Try again
        </button>
      </Notice>
    );
  }

  const user = me.data;

  if (!user.roles.includes('developer')) {
    return (
      <Notice eyebrow="Not a developer yet" title="Developer partners only">
        <p>
          You’re signed in as <strong className="text-ink">{user.email}</strong>, a player account.
          Apply to build on MOGZO: we review every application by hand.
        </p>
        <div className="flex flex-wrap gap-3">
          <a href={`${PLAYER_SITE}/developers/apply`} className={buttonClass('brand')}>
            Apply to build on MOGZO
          </a>
          <button
            type="button"
            onClick={() => authClient.signOut().finally(reload)}
            className={buttonClass('secondary')}
          >
            Sign out
          </button>
        </div>
      </Notice>
    );
  }

  if (!user.profileComplete) {
    return (
      <Notice eyebrow="One more step" title="Finish your profile first">
        <p>Add your month and year of birth on mogzo.com, then come back.</p>
        <a href={`${PLAYER_SITE}/complete-profile`} className={buttonClass('primary')}>
          Finish profile
        </a>
      </Notice>
    );
  }

  if (!user.twoFactorEnabled) {
    return (
      <Notice eyebrow="One more step" title="Turn on two-factor">
        <p>
          Developer accounts need two-factor authentication, because they can publish games to
          everyone. It takes a minute with an authenticator app.
        </p>
        <div className="flex flex-wrap gap-3">
          <a href={`${PLAYER_SITE}/account`} className={buttonClass('primary')}>
            Turn it on in Account
          </a>
          <button type="button" onClick={reload} className={buttonClass('secondary')}>
            It’s on now
          </button>
        </div>
      </Notice>
    );
  }

  return <DeveloperProvider me={user}>{children}</DeveloperProvider>;
}
